/**
 * Mangle — Layout Randomizer
 *
 * Applies seeded random offsets, angles, color shifts, and style changes
 * to content text objects while leaving watermarks in place.
 * Uses XORshift32 for deterministic results per seed.
 */

let _mangleSeed = 0;

/**
 * XORshift32 seeded PRNG. Deterministic per (seed, index) pair.
 */
function _mangleRng(seed, idx) {
  let s = seed ^ (idx * 2654435761);
  s ^= s << 13;
  s ^= s >> 17;
  s ^= s << 5;
  return (s >>> 0) / 4294967296;
}

/**
 * Apply mangle transforms to Fabric.js text objects on the canvas.
 *
 * @param {fabric.Canvas} canvas
 * @param {number} width - display canvas width
 * @param {number} height - display canvas height
 * @param {object} params - { spread, angle, colorShift, styleShift } each 0-100
 */
export function applyMangle(canvas, width, height, params = {}) {
  if (_mangleSeed === 0) return;

  const spread = (params.spread ?? 50) / 100;
  const angleProbability = (params.angle ?? 0) / 100;
  const colorProbability = (params.colorShift ?? 0) / 100;
  const styleProbability = (params.styleShift ?? 0) / 100;

  const margin = Math.round(width * 0.04);
  const xRange = width * 0.12 * spread;
  const yRange = height * 0.06 * spread;

  const accentColors = ['#ff4444', '#4a90d9', '#00cc88', '#ff6b35', '#9c27b0',
    '#e8a520', '#dc143c', '#00b8a9', '#ff00ff', '#00e5ff'];
  const styles = ['normal', 'bold'];
  const fontStyles = ['normal', 'italic'];

  // Collect mangle-able objects (skip internal objects and FX-locked items)
  const targets = [];
  canvas.getObjects().forEach((obj, i) => {
    if (!obj.data?.id) return;
    if (obj.type !== 'textbox') return;
    const id = obj.data.id;
    if (id.startsWith('__')) return; // skip watermarks, QR, wordmark
    if (obj.data.fxLocked) return;  // respect FX lock
    targets.push({ obj, i });
  });

  // Apply offsets
  for (const { obj, i } of targets) {
    // Spread: offset x/y
    if (spread > 0) {
      const dx = (_mangleRng(_mangleSeed, i * 7) - 0.5) * xRange;
      const dy = (_mangleRng(_mangleSeed, i * 7 + 1) - 0.5) * yRange;
      obj.set({
        left: Math.max(margin, Math.min(width - margin, obj.left + dx)),
        top: Math.max(margin, Math.min(height - margin, obj.top + dy)),
      });
    }

    // Angle: rotate some objects
    if (angleProbability > 0 && _mangleRng(_mangleSeed, i * 7 + 2) < angleProbability) {
      const maxAngle = 8 * angleProbability;
      const angle = (_mangleRng(_mangleSeed, i * 7 + 3) - 0.5) * 2 * maxAngle;
      obj.set('angle', angle);
    }

    // Color: shift some object colors
    if (colorProbability > 0 && _mangleRng(_mangleSeed, i * 7 + 4) < colorProbability) {
      const colorIdx = Math.floor(_mangleRng(_mangleSeed, i * 7 + 5) * accentColors.length);
      obj.set('fill', accentColors[colorIdx]);
    }

    // Style: change weight/style on some objects
    if (styleProbability > 0 && _mangleRng(_mangleSeed, i * 7 + 6) < styleProbability) {
      const weightIdx = Math.floor(_mangleRng(_mangleSeed, i * 11) * styles.length);
      const styleIdx = Math.floor(_mangleRng(_mangleSeed, i * 13) * fontStyles.length);
      obj.set('fontWeight', styles[weightIdx]);
      obj.set('fontStyle', fontStyles[styleIdx]);
    }
  }

  // --- Collision resolution ---
  // Sort by top position, then push overlapping objects downward.
  // Repeat up to 3 passes to resolve cascading overlaps.
  for (let pass = 0; pass < 3; pass++) {
    const sorted = targets
      .map(t => t.obj)
      .sort((a, b) => a.top - b.top);

    let moved = false;
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const curr = sorted[i];
      // Estimate the bottom of the previous object
      const prevBottom = prev.top + (prev.fontSize || 14) * 1.3 *
        ((prev.text?.split('\n').length || 1));
      const minGap = Math.max(4, height * 0.01);

      if (curr.top < prevBottom + minGap) {
        curr.set('top', Math.min(height - margin, prevBottom + minGap));
        moved = true;
      }
    }
    if (!moved) break;
  }

  canvas.renderAll();
}

/** Generate a random mangle seed and return it. */
export function mangleLayout() {
  _mangleSeed = Math.floor(Math.random() * 99999) + 1;
  return _mangleSeed;
}

/** Reset mangle (restore default positions on next hydration). */
export function resetMangle() {
  _mangleSeed = 0;
}

export function getMangleSeed() {
  return _mangleSeed;
}

export function setMangleSeed(seed) {
  _mangleSeed = seed;
}
